import React, { useState, useRef, useMemo } from 'react'
import Autocomplete from 'react-google-autocomplete'
import { observer } from 'mobx-react-lite'

// evertel
import Wrapper from './Wrapper'
import { Row, Collapse, Map, Button, Text } from '@evertel/web/ui'
import { MapValue, normalizeMapValue, hasValidCoords, DEFAULT_MAP_CENTER } from '@evertel/document'


export interface MapWriteWidgetProps {
    id: number|string
    htmlId?: string
    value: MapValue
    onChange?: (id: string|number, value: MapValue | null) => void
    title: string
    required?: boolean
    multiline?: boolean
    errors?: string
    scope?: string
    hint?: string
    placeholder?: string
    description?: string
    label?: string
}

const MapWriteWidget: React.FC<MapWriteWidgetProps> = observer(({
    id,
    htmlId,
    value,
    placeholder,
    onChange,
    title,
    required,
    scope,
    errors,
    ...otherProps
}) => {

    const inputRef = useRef<HTMLInputElement>(null)
    const [isMapLoaded, setIsMapLoaded] = useState(false)
    const [localError, setLocalError] = useState('')

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`
    const errorId = `error-${htmlId}`

    const combinedErrors = [errors, localError].filter(Boolean).join(' ')
    const hasErrors = combinedErrors.length > 0

    const normalized = useMemo(() => normalizeMapValue(value), [value])

    // address to show in the search box
    const displayText = normalized?.address || normalized?.displayName || ''

    // derive center directly from the current value — no local state needed
    // value flows back down from parent after every onChange
    // when no valid coords, explicitly center on default US position
    const hasCoords = hasValidCoords(normalized)
    const centerCoords = hasCoords ? normalized.geometry.location : DEFAULT_MAP_CENTER

    return (
        <Wrapper
            title={title}
            required={required}
            errors={combinedErrors || undefined}
            labelId={labelId}
            descriptionId={descriptionId}
            errorId={errorId}
            {...otherProps}
        >
            <Row className="position-relative schema-widget-map">
                { isMapLoaded &&
                    <Autocomplete
                        id={inputId}
                        ref={inputRef}
                        onPlaceSelected={(place) => {
                            if (!place?.geometry?.location) return

                            setLocalError('')

                            onChange?.(id, {
                                address: place.formatted_address,
                                geometry: {
                                    location: {
                                        lat: place.geometry.location.lat(),
                                        lng: place.geometry.location.lng()
                                    }
                                }
                            })
                        }}
                        defaultValue={displayText}
                        options={{
                            componentRestrictions: { country: 'us' },
                            types: [ 'geocode', 'establishment'],
                            fields: [ 'formatted_address', 'geometry', 'name' ]
                        }}
                        disabled={scope === 'schemaBuilder'}
                        className="form-control"
                        style={{
                            position: 'absolute',
                            zIndex: 1,
                            marginTop: 10,
                            marginLeft: 10,
                            width: '97%'
                        }}
                        placeholder={placeholder ?? 'Enter an address'}
                        aria-invalid={hasErrors ? true : undefined}
                        aria-errormessage={hasErrors ? errorId : undefined}
                        aria-required={required ? true : undefined}
                        aria-label="Location search"
                        onChange={() => {
                            // clear error when user starts typing again
                            if (localError) setLocalError('')
                        }}
                        onBlur={() => {
                            const inputText = inputRef.current?.value?.trim() || ''

                            if (inputText === '') {
                                // intentional clear
                                setLocalError('')
                                onChange?.(id, null)
                            } else if (inputText !== displayText) {
                                // typed something but didn't select from autocomplete
                                setLocalError('Please select an address by selecting from the choices or dragging the pin.')
                                onChange?.(id, null)
                            }
                        }}
                    />
                }
            </Row>
            <Row style={{height: (scope === 'schemaBuilder') ? '20vh' : '40vh'}}>
                <Map
                    center={centerCoords}
                    zoom={hasCoords ? 14 : 4}
                    onChange={(coords, { results }) => {
                        if (!results?.length) return

                        setLocalError('')

                        onChange?.(id, {
                            address: results[0].formatted_address,
                            geometry: {
                                location: coords
                            }
                        })
                        if (inputRef.current && scope !== 'schemaBuilder') {
                            inputRef.current.value = results[0].formatted_address
                        }
                    }}
                    onLoaded={() => {
                        setIsMapLoaded(true)
                    }}
                    aria-label="Interactive map"
                />
            </Row>
        </Wrapper>
    )
})

// ----------------------------------------------------------------------------------------

export interface MapReadWidgetProps {
    htmlId?: string
    value?: MapValue
    title?: string
}

const MapReadWidget: React.FC<MapReadWidgetProps> = ({
    htmlId,
    value,
    title,
    ...otherProps
}) => {

    const [showMap, setShowMap] = useState(false)

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`

    const normalizedValue = useMemo(() => normalizeMapValue(value), [value])

    const displayText = normalizedValue?.address || normalizedValue?.displayName || ''
    const hasCoords = hasValidCoords(normalizedValue)

    // show the address button if we have text to display
    const hasContent = displayText.length > 0

    return (
        <Wrapper
            value={normalizedValue}
            title={title}
            labelId={labelId}
            descriptionId={descriptionId}
            {...otherProps}
        >
            {hasContent ? (
                <>
                    <Button
                        id={inputId}
                        color="primary"
                        onClick={() => hasCoords && setShowMap(!showMap)}
                        disabled={!hasCoords}
                    >
                        {displayText}
                    </Button>
                    {hasCoords && (
                        <Collapse visible={showMap}>
                            <Row style={{height: '40vh'}}>
                                <Map
                                    initialPosition={normalizedValue.geometry.location}
                                    zoom={11}
                                    draggableMarker={false}
                                />
                            </Row>
                        </Collapse>
                    )}
                </>
            ) : (
                <Text id={inputId}>
                    <span className="visually-hidden">No value</span>
                    <span aria-hidden="true">---</span>
                </Text>
            )}
        </Wrapper>
    )
}

export {
    MapWriteWidget,
    MapReadWidget
}
